import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Ministry, MinistriesOverview, MinistryKind } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

export const KIND_TITLES: Record<MinistryKind, { title: string; hint: string }> = {
  grupo: { title: 'Grupos', hint: 'Cada miembro pertenece a uno: Varones, Damas, Jóvenes o Niños.' },
  raices: { title: 'Raíces (enseñanza)', hint: 'Todos los miembros están en Raíces; los del grupo Niños, en Raíces Kids. Es automático.' },
  servicio: { title: 'Ministerios de servicio', hint: 'Cada uno con su líder y co-líder.' },
};

function MinistryCard({ m }: { m: Ministry }) {
  return (
    <li>
      <Link to={`/app/ministerios/${m.id}`} className="ministry-card">
        <span className="ministry-card__head">
          <strong>{m.name}</strong>
        </span>
        <span className="small muted">
          {m.memberCount} {m.memberCount === 1 ? 'integrante' : 'integrantes'}
        </span>
        <span className="small">
          {m.leader ? (
            <>
              Líder: <strong>{m.leader.name}</strong>
            </>
          ) : (
            <span className="muted">Sin líder</span>
          )}
          {m.coLeaders.length > 0 ? ` · Co-líder: ${m.coLeaders.map((c) => c.name).join(', ')}` : ''}
        </span>
      </Link>
    </li>
  );
}

/** Personas sin grupo: la administración las asigna a Varones, Damas, Jóvenes o Niños. */
function WithoutGroupCard({ churchId, overview, onChanged }: { churchId: string; overview: MinistriesOverview; onChanged: () => void }) {
  const groups = overview.items.filter((m) => m.kind === 'grupo');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // El grupo elegido en cada lista; se guarda solo al tocar «Asignar».
  const [picked, setPicked] = useState<Record<string, string>>({});
  if (overview.withoutGroup.length === 0) return null;

  async function assign(personId: string, ministryId: string | undefined) {
    if (!ministryId) {
      setError('Elige primero el grupo.');
      return;
    }
    setBusy(personId);
    setError(null);
    try {
      await api.put(`/organization/churches/${churchId}/ministries/${ministryId}/members/${personId}`, {});
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title={`Sin grupo (${overview.withoutGroup.length})`}>
      <p className="muted small">Todo miembro debe pertenecer a un grupo. Asígnalos aquí:</p>
      {error ? <Alert>{error}</Alert> : null}
      <ul className="member-list">
        {overview.withoutGroup.map((p) => (
          <li key={p.personId} className="family-member">
            <span className="family-member__name">{p.name}</span>
            <label className="visually-hidden" htmlFor={`group-${p.personId}`}>
              Grupo de {p.name}
            </label>
            <select id={`group-${p.personId}`} value={picked[p.personId] ?? ''} disabled={busy === p.personId} onChange={(e) => setPicked({ ...picked, [p.personId]: e.target.value })}>
              <option value="">Elegir grupo…</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            <Button variant="secondary" busy={busy === p.personId} disabled={busy !== null || !picked[p.personId]} onClick={() => void assign(p.personId, picked[p.personId])} aria-label={`Asignar a ${p.name} al grupo`}>
              Asignar
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Ministerios de la iglesia: grupos, Raíces y ministerios de servicio. */
export function MinistriesPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync(
    (signal) => (churchId ? api.get<MinistriesOverview>(`/organization/churches/${churchId}/ministries`, signal) : Promise.resolve(null)),
    [churchId],
  );

  // Arriba los ministerios de la persona; abajo los demás, para consultar.
  const mine = data.status === 'ready' && data.data ? data.data.items.filter((m) => m.mine) : [];

  if (!churchId) {
    return (
      <div className="page">
        <h1>Ministerios</h1>
        <EmptyState title="Elige primero una iglesia en el selector de arriba." />
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__header page__header--actions">
        <div>
          <h1>Ministerios</h1>
          <p className="muted">{me.activeContext?.churchName}</p>
        </div>
        <Link to="/app/organigrama" className="btn btn--secondary">
          Organigrama
        </Link>
      </header>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data ? (
        <>
          {data.data.canAssignLeaders ? <WithoutGroupCard churchId={churchId} overview={data.data} onChanged={data.reload} /> : null}
          {mine.length > 0 ? (
            <section className="ministry-section" aria-labelledby="kind-mine">
              <h2 id="kind-mine">Mis ministerios</h2>
              <p className="small muted">Donde sirves o que diriges.</p>
              <ul className="ministry-grid">
                {mine.map((m) => (
                  <MinistryCard key={m.id} m={m} />
                ))}
              </ul>
            </section>
          ) : null}
          {mine.length > 0 && !data.data.canAssignLeaders ? <p className="small muted">Los demás ministerios de la iglesia, para que los conozcas:</p> : null}
          {(['grupo', 'raices', 'servicio'] as const).map((kind) => {
            const items = data.data!.items.filter((m) => m.kind === kind && !m.mine);
            if (items.length === 0) return null;
            return (
              <section key={kind} className="ministry-section" aria-labelledby={`kind-${kind}`}>
                <h2 id={`kind-${kind}`}>{KIND_TITLES[kind].title}</h2>
                <p className="small muted">{KIND_TITLES[kind].hint}</p>
                <ul className="ministry-grid">
                  {items.map((m) => (
                    <MinistryCard key={m.id} m={m} />
                  ))}
                </ul>
              </section>
            );
          })}
          {data.data.canAssignLeaders ? (
            <p className="small muted">Como administrador eliges al líder de cada ministerio; el líder elige a su co-líder.</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
